import { z } from 'zod';
import { TipoActividadEnum, type TipoActividad } from './tooltime.js';

// ---------------------------------------------------------------------------
// Existing OT-aggregated tooltime report (legacy single-period detalle por OT)
// ---------------------------------------------------------------------------

export const TooltimeReportQuerySchema = z.object({
  periodo: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Formato: YYYY-MM'),
  ambitoId: z.coerce.number().int().positive().optional(),
  areaId: z.coerce.number().int().positive().optional(),
});

export const TooltimeReportExportQuerySchema = z.object({
  periodo: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Formato: YYYY-MM'),
  ambitoId: z.coerce.number().int().positive().optional(),
  areaId: z.coerce.number().int().positive().optional(),
});

export interface TooltimeReportPorArea {
  area: string;
  areaId: number;
  horas: number;
  count: number;
}

export interface TooltimeReportDetalle {
  otId: number;
  correlativo: string;
  descripcion: string | null;
  area: string;
  totalHoras: number;
  registros: number;
}

export interface TooltimeReportResponse {
  periodo: string;
  totalHoras: number;
  totalRegistros: number;
  porArea: TooltimeReportPorArea[];
  detalle: TooltimeReportDetalle[];
  /** Audit counters so silent corrections are visible (e.g. how many entries hit the 12h cap). */
  meta?: { cappedEntries?: number };
}

export type TooltimeReportQuery = z.infer<typeof TooltimeReportQuerySchema>;
export type TooltimeReportExportQuery = z.infer<typeof TooltimeReportExportQuerySchema>;

// ---------------------------------------------------------------------------
// New legacy-parity reports: by-worker, by-activity (effective and all)
// Replace the legacy MySQL stored procedure `tooltimeKpi(option=3|4|5)`.
//
// Discriminated union over `mode` so the type system enforces "periodo
// requires periodo string", "rango requires both fechas". Removes the
// runtime-only superRefine guard.
// ---------------------------------------------------------------------------

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}(T.*)?$/;

const PeriodoModeShape = {
  mode: z.literal('periodo'),
  periodo: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Formato: YYYY-MM'),
} as const;

const RangoModeShape = {
  mode: z.literal('rango'),
  fechaInicio: z.string().regex(ISO_DATE_RE, 'Formato: YYYY-MM-DD'),
  fechaTermino: z.string().regex(ISO_DATE_RE, 'Formato: YYYY-MM-DD'),
} as const;

const SharedScopeShape = {
  ambitoId: z.coerce.number().int().positive().optional(),
  tipoOtId: z.coerce.number().int().positive().optional(),
} as const;

interface RangoOrderRefinable {
  mode: 'periodo' | 'rango';
  fechaInicio?: string;
  fechaTermino?: string;
}

function refineRangoOrder(data: RangoOrderRefinable, ctx: z.RefinementCtx): void {
  if (data.mode === 'rango' && data.fechaInicio && data.fechaTermino && data.fechaInicio > data.fechaTermino) {
    ctx.addIssue({ code: 'custom', message: 'fechaInicio debe ser <= fechaTermino', path: ['fechaTermino'] });
  }
}

export const TooltimeByWorkerQuerySchema = z
  .discriminatedUnion('mode', [
    z.object({ ...PeriodoModeShape, ...SharedScopeShape }),
    z.object({ ...RangoModeShape, ...SharedScopeShape }),
  ])
  .superRefine(refineRangoOrder);

export const TooltimeByActivityQuerySchema = z
  .discriminatedUnion('mode', [
    z.object({
      ...PeriodoModeShape,
      ...SharedScopeShape,
      efectivoOnly: z.coerce.boolean().optional().default(false),
    }),
    z.object({
      ...RangoModeShape,
      ...SharedScopeShape,
      efectivoOnly: z.coerce.boolean().optional().default(false),
    }),
  ])
  .superRefine(refineRangoOrder);

export type TooltimeByWorkerQuery = z.infer<typeof TooltimeByWorkerQuerySchema>;
export type TooltimeByActivityQuery = z.infer<typeof TooltimeByActivityQuerySchema>;

export interface TooltimeWorkerRow {
  usuarioId: number;
  nombre: string;
  /** True when nombre/apaterno were both blank in DB and we synthesized "Usuario #N" */
  nombreSynthetic: boolean;
  horasEfectivo: number;
  horasOtras: number;
  horasTotal: number;
  registros: number;
}

export interface TooltimeByWorkerResponse {
  mode: 'periodo' | 'rango';
  range: { inicio: string; termino: string };
  workers: TooltimeWorkerRow[];
  totals: {
    horasEfectivo: number;
    horasOtras: number;
    horasTotal: number;
    registros: number;
    workersCount: number;
  };
  /**
   * Audit/diagnostic metadata. `reason` is set when scopeAmbito returned an
   * empty payload (no ambitos). `cappedEntries` = entries truncated to the 12h
   * cap. `otsSinCuadrilla` = OTs in scope that contributed 0 because no crew is
   * loaded (known data gap until `cuadrilla_dotacion` is migrated) — surfaced so
   * an empty "Por Trabajador" reads as "crew missing", not "no work happened".
   * `otsFallbackSinFechas` = OTs where at least one crew member had NO recorded
   * work dates, so per-day attribution fell back to crediting all days (legacy/
   * un-migrated rows; the new per-day rule only refines OTs that recorded dates).
   * `otsConHorasSinAtribuir` = OTs with tooltime hours that no crew member's work
   * dates covered, so the hours were credited to nobody in this report (almost
   * always a data-entry slip: a tooltime day not added to the present crew's
   * "Fechas de trabajo"). Surfaced so the silence is observable, not lost.
   */
  meta?: {
    reason?: 'NO_AMBITOS_ASSIGNED';
    cappedEntries?: number;
    otsSinCuadrilla?: number;
    otsFallbackSinFechas?: number;
    otsConHorasSinAtribuir?: number;
  };
}

export interface TooltimeActivityRow {
  /** `null` represents the "Sin clasificar" bucket (legacy data without tipoActividad). */
  tipoActividad: TipoActividad | null;
  label: string;
  efectivo: boolean;
  horas: number;
  registros: number;
  pct: number;
}

export interface TooltimeByActivityResponse {
  mode: 'periodo' | 'rango';
  range: { inicio: string; termino: string };
  actividades: TooltimeActivityRow[];
  summary: {
    totalHoras: number;
    totalEfectivo: number;
    totalNoEfectivo: number;
    /** Hours with `tipoActividad = null` (legacy data pending backfill). */
    horasSinClasificar: number;
    pctEfectivo: number;
    totalRegistros: number;
  };
  /** `reason` set when no ambitos assigned. `cappedEntries` = entries truncated to the 12h cap. */
  meta?: { reason?: 'NO_AMBITOS_ASSIGNED'; cappedEntries?: number };
}

export { TipoActividadEnum };
export type { TipoActividad };
